import { Body, Link, VStack } from '@hover/blueprint';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import { IPricingSection } from '../interfaces/IPricingPageTableData';
import { IPricingTableHeader } from '../interfaces/IPricingTableHeader';
import { IPricingTableMobileAndTablet } from '../interfaces/IPricingTableMobileAndTablet';
import { IPricingToMatch } from '../interfaces/IPricingToMatch';
import { IPricingTooltip } from '../interfaces/IPricingTooltip';
import { URLS } from './marketingSiteLinks';

export const PRICING_BASIC_TOOLTIPS: IPricingTooltip[] = [
  {
    id: 1,
    title: 'Measurement PDF',
    tooltip:
      'Per property measurements are accurate to within 5% (in aggregate)',
  },
  {
    id: 2,
    title: 'Interactive 3D Model',
    tooltip:
      'Help homeowners understand their estimates and see their new roof, siding, and windows with real products on their homes.',
  },
  {
    id: 3,
    title: 'Unlimited Property Access',
    tooltip: 'Access all of your historical jobs in our secure cloud',
  },
  {
    id: 4,
    title: 'Design images',
    tooltip:
      'Boost homeowner confidence in the project with photorealistic images that show your customers exactly what their design choices with look like on their home.',
  },
  {
    id: 5,
    title: 'Basic data export',
    tooltip: 'Export all measurement data to XLS and PDF',
  },
  {
    id: 6,
    title: 'Direct ordering',
    tooltip:
      'Order electronically form leading suppliers with zero manual entry and without leaving the console',
  },
];

export const PRICING_FAQ = [
  {
    id: 1,
    title: 'Do I need to put a credit card on file when downloading HOVER?',
    content: (
      <VStack spacing={300} mb={500}>
        <Body size={500} fontWeight={400}>
          No, you can start using HOVER without putting a credit card on file.
          Your first job is included for free, but you need to add a credit card
          to your account to process subsequent jobs.
        </Body>
      </VStack>
    ),
  },
  {
    id: 2,
    title: 'Is there a monthly cost associated with HOVER?',
    content: (
      <Body size={500} fontWeight={400} mb={500}>
        HOVER pricing is a combination of a SaaS-based subscription plus per-job
        transactional charges. This entitles you to advanced features and volume
        discounts. If a membership is not right for you, you have the option to
        as you go on a per-property basis.
      </Body>
    ),
  },
  {
    id: 3,
    title: 'Can I try out a membership plan for free?',
    content: (
      <Body size={500} fontWeight={400} mb={500}>
        Yes, you can start a free 30 day trial of the Measure Plan. Your first
        job is included for free, and you need to add a credit card to your
        account to process subsequent jobs.
      </Body>
    ),
  },
  {
    id: 4,
    title: 'How much does it cost to sign up with HOVER?',
    content: (
      <Body size={500} fontWeight={400} mb={500}>
        You can download the HOVER app from the App Store or Google Play at no
        cost. You’ll also receive your first property for free so you can try
        HOVER 100% risk-free.
      </Body>
    ),
  },
  {
    id: 5,
    title: 'How can I receive access to volume discounts?',
    content: (
      <Body size={500} fontWeight={400} mb={500}>
        If you sign up for one of our membership plans, you would receive per
        job discounts of $10-$15, depending on which plan you selected. Give us
        a call at (888) 868-8279 or{' '}
        <Link href="/">schedule a time to chat</Link>.
      </Body>
    ),
  },
];

export const PRICING_TO_MATCH_PROPERTY: IPricingToMatch[] = [
  {
    id: 1,
    title: 'SIMPLE',
    facets: '1-4 Facets',
    roofOnly: 25,
    complete: 50,
    completeBlueprint: 150,
    image: (
      <StaticImage
        placeholder="blurred"
        src="../assets/images/pricing_simple.png"
        alt="simple pricing"
        objectFit="contain"
      />
    ),
  },
  {
    id: 2,
    title: 'AVERAGE',
    facets: '5-10 Facets',
    roofOnly: 50,
    complete: 75,
    completeBlueprint: 175,
    image: (
      <StaticImage
        placeholder="blurred"
        src="../assets/images/pricing_average.png"
        alt="average pricing"
        objectFit="contain"
      />
    ),
  },
  {
    id: 3,
    title: 'COMPLEX',
    facets: '11+ Facets',
    roofOnly: 75,
    complete: 99,
    completeBlueprint: 199,
    image: (
      <StaticImage
        placeholder="blurred"
        src="../assets/images/pricing_complex.png"
        alt="complex pricing"
        objectFit="contain"
      />
    ),
  },
];

export const PRICING_TABLE_DATA: IPricingSection[] = [
  {
    sectionName: 'Foundations',
    features: [
      {
        feature: 'Property discounts',
        tooltip:
          'Unlock Premium features by contacting us t receive customized per property discounts based on your volume',
        values: [
          'None',
          '$10 off all jobs',
          '$15 off all jobs',
          '$15 off all jobs',
        ],
      },
      {
        feature: 'Property access',
        tooltip:
          'Access all of your properties, measurements, and models in our secure cloud',
        values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'],
      },
      {
        feature: 'Measurement PDF',
        tooltip:
          'Industry-leading accuracy is presented in a familiar PDF deliverable',
        values: [true, true, true, true],
      },
      {
        feature: 'Interactive 3D Model',
        tooltip: 'View real products from real manufacturers on the 3D model',
        values: [true, true, true, true],
      },
      {
        feature: 'Multi-platform Access',
        tooltip:
          'Access your measurements, 3D models, photos and notes from our iOS, Android, and web apps.',
        values: [true, true, true, true],
      },
      {
        feature: 'Design images',
        tooltip:
          'Boost homeowner confidence in the project with photorealistic images that show your customers exactly what their design choices with look like on their home.',
        values: ['+$350', '+$350', '+$350', '+$350'],
      },
      {
        feature: 'Upload blueprints',
        tooltip:
          'Upload a blueprint file to get all your HOVER deliverables (including measurements and 3D model) for unbuilt structures.',
        values: [true, true, true, true],
      },
      {
        feature: 'Capture only',
        tooltip:
          'Capture and store unlimited property photos at no cost, and order the measurements and 3D model when you are ready',
        values: [true, true, true, true],
      },
      {
        feature: 'API access',
        tooltip:
          'Programmatic access to our ecosystem to build robust integrations with other software systems in order to streamline workflows and save time',
        values: [true, true, true, true],
      },
      {
        feature: 'Data exports',
        tooltip:
          'Export measurement and 3D model data from HOVER in file formats usable by other software in your existing workflow',
        values: [
          'PDF, XLS',
          'PDF, XLS, XML, JSON',
          'PDF, XLS, XML, JSON, SKP, DWG, DXF, ESX',
          'PDF, XLS, XML, JSON, SKP, DWG, DXF, ESX',
        ],
      },
      {
        feature: 'Expedited job processing',
        tooltip:
          'Jump higher in the queue to get time-sensitive jobs returned more quickly (additional fee)',
        values: ['+$40', '+$30', '+$20', '+$20'],
      },
      {
        feature: 'Photo capture',
        tooltip:
          'Use your smartphone to take ground-level photos of a property which will be turned into measurements and a 3D model',
        values: [true, true, true, true],
      },
      {
        feature: 'Direct ordering',
        tooltip:
          'Order electronically form leading suppliers with zero manual entry and without leaving the console',
        values: [true, true, true, true],
      },
    ],
  },
  {
    sectionName: 'Workflow Enhancement',
    features: [
      {
        feature: 'Invite others to capture',
        tooltip:
          'Invite anyone to photograph a property for you using HOVER Connect, for which you will receive full measurements and a 3D model',
        values: [false, true, true, true],
      },
      {
        feature: 'Personalized branding',
        tooltip: 'Add your logo to all measurement PDFs',
        values: [false, true, true, true],
      },
      {
        feature: 'Gallery and screenshot mode',
        tooltip:
          'Add real products from real manufacturers to 3D models of any property, create multiple versions, and showcase these options to clients',
        values: [false, true, true, true],
      },
      {
        feature: 'Integrations (contractors)',
        tooltip:
          'Eliminate manual data entry by integrating HOVER into your existing CRM, Job management, and financing software - JobNimbus, JobProgress, Leap to Digital, CompanyCam, Zapier, i360, Restoration AI, SalesRabbit, Hatch, GAF Project, Mosaic',
        values: [false, true, true, true],
      },
      {
        feature: 'Integrations (Insurance)',
        tooltip:
          'Eliminate manual data entry by integrating HOVER into your existing claims management and estimation software - CoreLogic (Symbility), OnSeen, Accurence, FileTrac, ClaimAssist, VCA',
        values: [false, true, true, true],
      },
    ],
  },
  {
    sectionName: 'Sale and Production Automation',
    features: [
      {
        feature: 'Estimates for Roofing Trades',
        tooltip:
          'Accurate estimates for multiple trades in seconds that reflect job complexity and your unique pricing',
        values: [false, false, false, true],
      },
      {
        feature: 'Estimates for Siding Trades',
        tooltip:
          'Build an accurate siding estimate for all job complexities in seconds with material libraries that reflect your unique pricing.',
        values: [false, false, false, true],
      },
      {
        feature: 'Proposals and Digital Signatures',
        tooltip:
          'Automatically populate your contract templates and get digital signatures while onsite',
        values: [false, false, false, true],
      },
      {
        feature: 'Production Console',
        tooltip:
          'Demystify and speed up project management with all the dependable resources you need to produce a job; from detailed takeoffs to measurements and job economic',
        values: [false, false, false, true],
      },
      {
        feature: 'Material Lists',
        tooltip: 'Precise, pre-populated, material lists for multiple trades',
        values: [false, false, false, true],
      },
      {
        feature: 'Labor Lists',
        tooltip:
          'Generate work orders in a click that reflect a project’s specific installation needs',
        values: [false, false, false, true],
      },
      {
        feature: 'Customization Services',
        tooltip:
          'White glove service to customize HOVER features to reflect your pricing and process in weeks, not months',
        values: [false, false, false, true],
      },
    ],
  },
  {
    sectionName: 'Enterprise Features',
    features: [
      {
        feature: 'Priority Support',
        tooltip: 'Jump to the front of the line with priority support queues',
        values: [false, false, true, true],
      },
      {
        feature: 'Priority Job Processing',
        tooltip:
          'Unlock quicker average delivery without paying for expedited job processing',
        values: [false, false, true, true],
      },
      {
        feature: 'Assigned Account Manager',
        tooltip:
          'Talk to an assigned Account Manager for questions about the product or your business',
        values: [false, false, true, true],
      },
      {
        feature: 'Single Sign-on (SSO)',
        tooltip:
          'Enable enterprise-grade security and authentication for your entire team',
        values: [false, false, true, true],
      },
      {
        feature: 'Advanced Admin and User Permissions',
        tooltip:
          'Control access to certain features for third parties and internal team members',
        values: [false, false, true, true],
      },
    ],
  },
];

export const PRICING_PAGE_TABLE_HEADER: IPricingTableHeader[] = [
  {
    id: 1,
    name: 'PAY AS YOU GO',
    description: 'Start with measurements and access to basic features.',
    buttonText: 'Start free trial',
    background: 'brandBrown.100',
    type: 'The Basics',
  },
  {
    id: 2,
    name: 'MEASURE',
    description:
      'For individuals and small businesses who want to integrate their workflows.',
    buttonText: 'Contact sales',
    linkTo: URLS.pricing.contactSales,
    background: 'brandTan.200',
    type: 'Premium',
  },
  {
    id: 3,
    name: 'SCALE',
    description:
      'For businesses who need access to advanced features that accommodate larger job volumes.',
    buttonText: 'Contact sales',
    linkTo: URLS.pricing.contactSales,
    background: 'brandGreen.100',
    type: 'Premium',
  },
  {
    id: 4,
    name: 'TRANSFORM',
    description:
      'For businesses looking to power their end to end workflow from sales to production.',
    buttonText: 'Contact sales',
    linkTo: URLS.pricing.contactSales,
    background: 'primary.100',
    type: 'Premium',
  },
];

const PRICING_TABLE_TABLET_AND_MOBILE: IPricingTableMobileAndTablet[] = [
  {
    id: 1,
    type: 'PAY AS YOU GO',
    values: [],
  },
  {
    id: 2,
    type: 'MEASURE',
    values: [],
  },
  {
    id: 3,
    type: 'SCALE',
    values: [],
  },
  {
    id: 4,
    type: 'TRANSFORM',
    values: [],
  },
];

PRICING_TABLE_DATA.forEach(section => {
  section.features.forEach(feature => {
    feature.values.forEach((value, index) =>
      PRICING_TABLE_TABLET_AND_MOBILE[index].values.push(value),
    );
  });
});

export { PRICING_TABLE_TABLET_AND_MOBILE };

export const PRICING_PREMIUM_TOOLTIPS: IPricingTooltip[] = [
  {
    id: 1,
    title: 'Advanced export files',
    subTitle: '(.esx, .skp, .dwg, .dxf)',
    tooltip:
      'Export all measurement data to XLS, JSON, XML and PDF and all 3D model data to popular CAD types: SKP, DXF, DWG, ESX',
  },
  {
    id: 2,
    title: 'Software Integrations',
    tooltip:
      'Eliminate manual data entry by integrating seamlessly with JobNimbus, JobProgress, Acculynx, and CoreLogic, among others',
  },
  {
    id: 3,
    title: 'Roofing and Siding Estimates',
    tooltip:
      'Accurate estimates for multiple trades in seconds that reflect job complexity and your unique pricing',
  },
  {
    id: 4,
    title: 'Proposals and contracts',
    tooltip:
      'Automatically populate your contract templates and get digital signatures while onsite',
  },
  {
    id: 5,
    title: 'Priority dedicated support',
    tooltip:
      'Jump to the front of the line with priority support queues. Talk to an assigned Account Manager for questions about the product or your business.',
  },
];
